/**
 * rip-off.se — front-end behaviour.
 *
 * Vanilla JS, no framework. Each feature is a small init function guarded on the
 * presence of its DOM hook, so this file is safe to load site-wide. Files in
 * src/js/ are NOT bundled together by gulp — each becomes its own static file —
 * so shared behaviour lives here in main.js.
 */
(function () {
	'use strict';

	/**
	 * Mobile navigation toggle.
	 * Hooks: [data-nav-toggle], [data-nav-panel]. Manages aria-expanded and a
	 * body scroll lock while the panel is open.
	 */
	function initMobileNav() {
		const toggle = document.querySelector('[data-nav-toggle]');
		const panel = document.querySelector('[data-nav-panel]');
		if (!toggle || !panel) return;

		const setOpen = (open) => {
			toggle.setAttribute('aria-expanded', String(open));
			panel.classList.toggle('is-open', open);
			document.body.classList.toggle('overflow-hidden', open);
		};

		toggle.addEventListener('click', () => {
			const open = toggle.getAttribute('aria-expanded') === 'true';
			setOpen(!open);
		});

		// Close on link click and on Escape.
		panel.addEventListener('click', (e) => {
			if (e.target.closest('a')) setOpen(false);
		});
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') setOpen(false);
		});
	}

	/**
	 * Adds a class to the header once the page is scrolled, so it can gain a
	 * solid background. Hook: [data-site-header].
	 */
	function initScrollHeader() {
		const header = document.querySelector('[data-site-header]');
		if (!header) return;

		const onScroll = () => {
			header.classList.toggle('is-scrolled', window.scrollY > 80);
		};
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
	}

	/**
	 * Accessible playlist audio player with a single shared <audio> element.
	 * The <audio> is preload="none" and its src is only set on play, so none of
	 * the tracks download until the visitor asks for one.
	 *
	 * Hooks under [data-audio-player]:
	 *   [data-audio-el] [data-audio-playpause] [data-audio-current]
	 *   [data-audio-seek] [data-audio-elapsed] [data-audio-duration]
	 *   [data-audio-volume] [data-audio-mute] [data-audio-track]*
	 */
	function initAudioPlayers() {
		const analytics = window.RipoffMusicAnalytics || null;
		const sessionId = (() => {
			try {
				const key = 'ripoff_music_session';
				let id = window.sessionStorage.getItem(key);
				if (!id) {
					id = (window.crypto?.randomUUID?.() || String(Date.now()) + '-' + Math.random().toString(16).slice(2));
					window.sessionStorage.setItem(key, id);
				}
				return id;
			} catch (e) {
				return String(Date.now()) + '-' + Math.random().toString(16).slice(2);
			}
		})();

		const sendAnalytics = (eventType, track, duration) => {
			if (navigator.webdriver) return;
			if (!analytics?.endpoint || !analytics?.nonce || !track) return;

			const data = new FormData();
			data.set('action', 'ripoff_music_event');
			data.set('nonce', analytics.nonce);
			data.set('event_type', eventType);
			data.set('track_id', track.dataset.trackId || '0');
			data.set('track_title', track.dataset.title || '');
			data.set('track_kind', track.dataset.kind || 'unknown');
			// Tracks are identified by attachment ID only — never the file URL.
			data.set('session_id', sessionId);
			data.set('page_url', window.location.href);
			data.set('duration_seconds', String(Math.max(0, Math.round(duration || 0))));

			if (navigator.sendBeacon) {
				navigator.sendBeacon(analytics.endpoint, data);
				return;
			}

			fetch(analytics.endpoint, {
				method: 'POST',
				body: data,
				credentials: 'same-origin',
				keepalive: true,
			}).catch(() => {});
		};

		const fmt = (s) => {
			if (!isFinite(s) || s < 0) s = 0;
			const m = Math.floor(s / 60);
			const sec = Math.floor(s % 60);
			return m + ':' + String(sec).padStart(2, '0');
		};

		document.querySelectorAll('[data-audio-player]').forEach((root) => {
			const audio = root.querySelector('[data-audio-el]');
			const playBtn = root.querySelector('[data-audio-playpause]');
			const currentEl = root.querySelector('[data-audio-current]');
			const iconPlay = root.querySelector('[data-icon-play]');
			const iconPause = root.querySelector('[data-icon-pause]');
			const seek = root.querySelector('[data-audio-seek]');
			const elapsedEl = root.querySelector('[data-audio-elapsed]');
			const durationEl = root.querySelector('[data-audio-duration]');
			const volume = root.querySelector('[data-audio-volume]');
			const muteBtn = root.querySelector('[data-audio-mute]');
			const tracks = Array.from(root.querySelectorAll('[data-audio-track]'));
			if (!audio || !tracks.length) return;

			const tokenBase = root.dataset.tokenBase || '';
			let current = -1;
			let seeking = false;
			let completedSent = false;
			let loadSeq = 0; // guards against out-of-order token responses

			// Fetch a short-lived signed stream URL for an attachment id.
			// The real uploads URL is never in the page — only the id is.
			const resolveStreamUrl = async (id) => {
				const res = await fetch(tokenBase + encodeURIComponent(id), {
					credentials: 'same-origin',
					headers: { 'Accept': 'application/json' },
				});
				if (!res.ok) throw new Error('token ' + res.status);
				const data = await res.json();
				if (!data || !data.url) throw new Error('no url');
				return data.url;
			};

			const setIcons = (playing) => {
				if (iconPlay) iconPlay.classList.toggle('hidden', playing);
				if (iconPause) iconPause.classList.toggle('hidden', !playing);
				if (playBtn) playBtn.setAttribute('aria-label', playing ? 'Pausa' : 'Spela');
			};

			const markActive = (idx) => {
				tracks.forEach((t, i) => {
					const active = i === idx;
					t.classList.toggle('is-active', active);
					if (active) t.setAttribute('aria-current', 'true');
					else t.removeAttribute('aria-current');
				});
			};

			const load = (idx, autoplay) => {
				const t = tracks[idx];
				if (!t) return;
				current = idx;
				completedSent = false;
				if (currentEl) currentEl.textContent = t.dataset.title || '';
				markActive(idx);
				if (seek) seek.value = 0;
				if (elapsedEl) elapsedEl.textContent = '0:00';

				// Resolve a fresh signed URL by attachment id, then set src.
				// preload stays "none" until we have a URL → nothing downloads early.
				const seq = ++loadSeq;
				const id = t.dataset.trackId;
				resolveStreamUrl(id)
					.then((url) => {
						if (seq !== loadSeq) return; // a newer selection won
						audio.src = url;
						if (autoplay) {
							sendAnalytics('started', t, 0);
							audio.play().catch(() => {});
						}
					})
					.catch(() => { /* leave player idle on failure */ });
			};

			// Play/pause button
			playBtn?.addEventListener('click', () => {
				if (current === -1) { load(0, true); return; }
				if (audio.paused) audio.play().catch(() => {}); else audio.pause();
			});

			// Track selection (also keyboard-activatable since they're <button>s)
			tracks.forEach((t, i) => {
				t.addEventListener('click', () => {
					if (i === current && !audio.paused) { audio.pause(); return; }
					if (i === current && audio.paused) { audio.play().catch(() => {}); return; }
					load(i, true);
				});
			});

			// Time / seekbar
			audio.addEventListener('loadedmetadata', () => {
				if (durationEl) durationEl.textContent = fmt(audio.duration);
			});
			audio.addEventListener('timeupdate', () => {
				if (seeking) return;
				if (elapsedEl) elapsedEl.textContent = fmt(audio.currentTime);
				if (seek && audio.duration) seek.value = String((audio.currentTime / audio.duration) * 100);
			});
			if (seek) {
				const scrub = () => { seeking = true; if (elapsedEl && audio.duration) elapsedEl.textContent = fmt((seek.value / 100) * audio.duration); };
				const commit = () => { if (audio.duration) audio.currentTime = (seek.value / 100) * audio.duration; seeking = false; };
				seek.addEventListener('input', scrub);
				seek.addEventListener('change', commit);
			}

			// Volume + mute
			if (volume) {
				volume.addEventListener('input', () => { audio.volume = parseFloat(volume.value); audio.muted = false; });
			}
			muteBtn?.addEventListener('click', () => {
				audio.muted = !audio.muted;
				muteBtn.setAttribute('aria-pressed', String(audio.muted));
				if (volume) volume.value = audio.muted ? '0' : String(audio.volume || 1);
			});

			// State + auto-advance
			audio.addEventListener('play', () => setIcons(true));
			audio.addEventListener('pause', () => setIcons(false));
			audio.addEventListener('ended', () => {
				if (!completedSent && current > -1) {
					completedSent = true;
					sendAnalytics('completed', tracks[current], audio.duration || audio.currentTime || 0);
				}
				if (current + 1 < tracks.length) load(current + 1, true); else setIcons(false);
			});
		});
	}

	/**
	 * Hero background slideshow — soft crossfade between decorative images.
	 *
	 * Hook: [data-hero-slideshow] containing [data-hero-slide] children; the
	 * active slide has class `is-active`. Autoplay only runs when the container
	 * has [data-hero-autoplay] (i.e. more than one slide). Behaviour:
	 *   - Bails on fewer than 2 slides → no JS, single image stays put.
	 *   - [data-hero-random]: starts on a random slide, then continues in the
	 *     gallery's order and loops (no shuffle between changes).
	 *   - No initial fade: the first visible image appears instantly (transitions
	 *     are suspended while the start slide is chosen), so there's no crossfade
	 *     or blink on load. The first change happens only at the first timed tick.
	 *   - prefers-reduced-motion: reduce → still cycles on the timer, but CSS
	 *     removes all transition/transform/animation so images swap INSTANTLY.
	 *     The JS is motion-agnostic: it only toggles `is-active`; whether that
	 *     swap animates is decided entirely in CSS.
	 *   - Pauses while the tab is hidden; resumes on focus (both motion modes).
	 *   - Idempotent: marks the root so a second init call is a no-op.
	 */
	function initHeroSlideshow() {
		document.querySelectorAll('[data-hero-slideshow]').forEach((root) => {
			if (root.dataset.heroInit === 'done') return; // no double-init
			root.dataset.heroInit = 'done';

			const slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
			// Single (or no) slide, or autoplay not requested → no timer at all.
			if (slides.length < 2 || !root.hasAttribute('data-hero-autoplay')) return;

			// Per-hero settings from data attributes — nothing hardcoded here.
			// Slide duration in ms, clamped defensively; falls back to 7s.
			const durSec = parseFloat(root.dataset.slideDuration);
			const INTERVAL = Math.max(2000, Math.min(30000,
				(isFinite(durSec) && durSec > 0 ? durSec : 7) * 1000));

			// Current active slide (PHP marks slide 0). This is what the browser
			// has already painted opaque, so it's a safe, flash-free starting point.
			let index = slides.findIndex((s) => s.classList.contains('is-active'));
			if (index < 0) { index = 0; slides[0].classList.add('is-active'); }

			// Optional random START only: pick a slide, then continue sequentially.
			// Suspend transitions while we move `is-active` so the switch from the
			// server-rendered slide 0 to the random start causes NO transition/blink.
			// (Works in both motion modes.)
			if (root.hasAttribute('data-hero-random')) {
				const startIndex = Math.floor(Math.random() * slides.length);
				if (startIndex !== index) {
					root.classList.add('is-no-transition');
					slides[index].classList.remove('is-active');
					slides[startIndex].classList.add('is-active');
					index = startIndex;
					// Force a reflow so the class change takes effect with no
					// transition, then re-enable transitions for future changes.
					void root.offsetWidth;
					root.classList.remove('is-no-transition');
				}
			}

			let timer = null;

			const show = (next) => {
				if (next === index) return;
				slides[index].classList.remove('is-active');
				slides[next].classList.add('is-active');
				index = next;
			};
			const advance = () => show((index + 1) % slides.length);

			// (Re)start the timer. Always clears any existing one first so a resume
			// can't leave a stale/throttled interval running, and so the full slide
			// duration always elapses before the FIRST change (incl. after random
			// start) — never a short first tick. Runs in BOTH motion modes; only
			// the visual transition differs (CSS), not whether images cycle.
			const start = () => {
				if (timer !== null) window.clearInterval(timer);
				timer = window.setInterval(advance, INTERVAL);
			};
			const stop = () => {
				if (timer !== null) { window.clearInterval(timer); timer = null; }
			};

			// Pause when the tab is hidden; resume (fresh timer) when visible again.
			document.addEventListener('visibilitychange', () => {
				if (document.hidden) stop(); else start();
			});

			// Kick off autoplay. First change happens after one full INTERVAL.
			start();
		});
	}

	/**
	 * YouTube video carousel — one video at a time, prev/next + swipe + dots.
	 *
	 * Hook: [data-video-carousel] with [data-video-slide] children (each has
	 * [data-video-play], data-video-id, data-video-title), optional
	 * [data-video-prev]/[data-video-next] and [data-video-dot].
	 *
	 * Performance/privacy: the actual player is a privacy-enhanced
	 * youtube-nocookie iframe injected ONLY on a play click (click-to-load), so
	 * no YouTube resources load on page load. No autoplay, no auto-advance.
	 * Playing a video and then navigating away tears the iframe back down to the
	 * poster, so only one player ever exists and nothing keeps running.
	 */
	function initVideoCarousel() {
		document.querySelectorAll('[data-video-carousel]').forEach((root) => {
			if (root.dataset.videoInit === 'done') return;
			root.dataset.videoInit = 'done';

			const slides = Array.from(root.querySelectorAll('[data-video-slide]'));
			if (!slides.length) return;

			const dots = Array.from(root.querySelectorAll('[data-video-dot]'));
			let index = slides.findIndex((s) => s.classList.contains('is-active'));
			if (index < 0) index = 0;

			// Replace a slide's poster button with the nocookie iframe (click-to-load).
			const loadPlayer = (slide) => {
				if (slide.querySelector('iframe')) return; // already loaded
				const id = slide.dataset.videoId;
				const title = slide.dataset.videoTitle || 'YouTube video';
				if (!id) return;
				const frame = document.createElement('iframe');
				frame.className = 'ripoff-videos__iframe';
				// Privacy-enhanced host + rel=0; autoplay=1 only because the user
				// just clicked play (a user gesture), never on load.
				frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
					'?rel=0&modestbranding=1&playsinline=1&autoplay=1';
				frame.title = title;
				frame.setAttribute('loading', 'lazy');
				frame.setAttribute('frameborder', '0');
				frame.setAttribute('allow', 'accelerometer; encrypted-media; gyroscope; picture-in-picture');
				frame.setAttribute('allowfullscreen', '');
				const poster = slide.querySelector('[data-video-play]');
				if (poster) poster.replaceWith(frame); else slide.appendChild(frame);
			};

			// Tear an already-loaded iframe back down so it stops playing when we
			// leave the slide. The original poster markup is re-rendered by simply
			// reloading is overkill; instead we hide via CSS (.is-active) and remove
			// the iframe so audio never continues in the background.
			const unloadPlayer = (slide) => {
				const frame = slide.querySelector('iframe');
				if (frame) frame.remove();
			};

			const show = (next) => {
				next = (next + slides.length) % slides.length;
				if (next === index) return;
				unloadPlayer(slides[index]); // stop any playing video we're leaving
				slides[index].classList.remove('is-active');
				slides[index].setAttribute('aria-hidden', 'true');
				slides[next].classList.add('is-active');
				slides[next].removeAttribute('aria-hidden');
				if (dots[index]) { dots[index].classList.remove('is-active'); dots[index].setAttribute('aria-selected', 'false'); }
				if (dots[next]) { dots[next].classList.add('is-active'); dots[next].setAttribute('aria-selected', 'true'); }
				index = next;
			};

			// Play buttons (click-to-load).
			slides.forEach((slide) => {
				const btn = slide.querySelector('[data-video-play]');
				btn?.addEventListener('click', () => loadPlayer(slide));
				// Poster thumbnail: fall back from maxres to hq if maxres 404s
				// (maxresdefault doesn't exist for every video).
				const thumb = slide.querySelector('[data-video-thumb]');
				if (thumb) {
					thumb.addEventListener('error', function onErr() {
						thumb.removeEventListener('error', onErr);
						thumb.src = 'https://i.ytimg.com/vi/' + thumb.dataset.videoThumb + '/hqdefault.jpg';
					});
				}
			});

			// Prev / next.
			root.querySelector('[data-video-prev]')?.addEventListener('click', () => show(index - 1));
			root.querySelector('[data-video-next]')?.addEventListener('click', () => show(index + 1));

			// Dots.
			dots.forEach((dot) => {
				dot.addEventListener('click', () => show(parseInt(dot.dataset.videoDot, 10) || 0));
			});

			// Keyboard: left/right arrows navigate when the carousel has focus.
			root.addEventListener('keydown', (e) => {
				if (e.key === 'ArrowLeft') { show(index - 1); }
				else if (e.key === 'ArrowRight') { show(index + 1); }
			});

			// Swipe on touch devices.
			if (slides.length > 1) {
				const track = root.querySelector('[data-video-track]') || root;
				let startX = 0, startY = 0, tracking = false;
				track.addEventListener('touchstart', (e) => {
					const t = e.changedTouches[0];
					startX = t.clientX; startY = t.clientY; tracking = true;
				}, { passive: true });
				track.addEventListener('touchend', (e) => {
					if (!tracking) return;
					tracking = false;
					const t = e.changedTouches[0];
					const dx = t.clientX - startX;
					const dy = t.clientY - startY;
					// Horizontal, decisive swipe only (ignore vertical scrolls).
					if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
						show(dx < 0 ? index + 1 : index - 1);
					}
				}, { passive: true });
			}
		});
	}

	/**
	 * Scroll reveal — subtle, premium, once per element (Final Art Direction v1.2).
	 *
	 * Reveals [data-reveal] / [data-reveal-stagger] section-content groups as they
	 * enter the viewport by adding `.is-revealed`. The hidden start state lives in
	 * CSS and only applies under the `js-reveal` class on <html> (set here) AND
	 * prefers-reduced-motion: no-preference — so without JS, or with reduced
	 * motion, content is always fully visible (progressive enhancement).
	 *
	 * Elements already in view on load reveal immediately (no flash). Uses a
	 * single IntersectionObserver; unobserves after the first reveal (once only).
	 */
	function initScrollReveal() {
		const targets = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
		if (!targets.length) return;

		const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		// No IntersectionObserver support, or reduced motion → reveal everything now.
		if (reduce || typeof IntersectionObserver === 'undefined') {
			targets.forEach((el) => el.classList.add('is-revealed'));
			return;
		}

		const io = new IntersectionObserver((entries, obs) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					entry.target.classList.add('is-revealed');
					obs.unobserve(entry.target); // once only
				}
			});
		}, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

		targets.forEach((el) => io.observe(el));

		// Failsafe: never let content stay hidden. If anything hasn't revealed
		// within a few seconds (e.g. it never scrolls into view, or the observer
		// didn't fire), reveal it anyway. Content visibility must not depend on
		// the animation succeeding.
		window.setTimeout(() => {
			targets.forEach((el) => {
				if (!el.classList.contains('is-revealed')) {
					el.classList.add('is-revealed');
					io.unobserve(el);
				}
			});
		}, 3000);
	}

	/**
	 * Copy-to-clipboard for the Swish number on the support page. Progressive
	 * enhancement: the number is always visible as text; this just adds a
	 * one-click copy. Shows a brief "Copied!" confirmation. No dependency.
	 */
	function initSwishCopy() {
		document.querySelectorAll('[data-swish-copy]').forEach((btn) => {
			const value = btn.getAttribute('data-swish-value') || '';
			const done = btn.parentElement
				? btn.parentElement.querySelector('[data-swish-copied]')
				: null;
			if (!value) return;

			btn.addEventListener('click', function () {
				const confirm = () => {
					if (done) {
						done.hidden = false;
						window.setTimeout(() => { done.hidden = true; }, 2500);
					}
				};
				if (navigator.clipboard && navigator.clipboard.writeText) {
					navigator.clipboard.writeText(value).then(confirm).catch(() => {});
				} else {
					// Fallback for older browsers: temporary textarea + execCommand.
					try {
						const ta = document.createElement('textarea');
						ta.value = value;
						ta.setAttribute('readonly', '');
						ta.style.position = 'absolute';
						ta.style.left = '-9999px';
						document.body.appendChild(ta);
						ta.select();
						document.execCommand('copy');
						document.body.removeChild(ta);
						confirm();
					} catch (e) { /* no-op */ }
				}
			});
		});
	}

	/**
	 * Click-to-load for generic third-party embeds in page content (currently
	 * the SoundCloud player on Egen musik / Original Music). App\Embeds rewrites
	 * the iframe into a placeholder carrying the real src in data-embed-src; we
	 * build the iframe only when the visitor clicks, so nothing is fetched from
	 * the third party on initial load. No autoplay is added.
	 */
	function initEmbedLoad() {
		document.querySelectorAll('[data-embed-load]').forEach((box) => {
			const btn = box.querySelector('[data-embed-load-btn]');
			const src = box.getAttribute('data-embed-src');
			if (!btn || !src) return;

			btn.addEventListener('click', function () {
				const iframe = document.createElement('iframe');
				iframe.src = src; // reading the attribute via the DOM decodes entities
				iframe.width = '100%';
				iframe.height = box.getAttribute('data-embed-height') || '550';
				iframe.setAttribute('frameborder', 'no');
				iframe.setAttribute('scrolling', 'no');
				iframe.setAttribute('loading', 'lazy');
				box.replaceChildren(iframe);
			}, { once: true });
		});
	}

	/**
	 * Click-to-load for the Instagram feed. The QuadLayers feed fetches ~60 MB
	 * (images + autoplay video reels) from Instagram's CDN as soon as its markup
	 * is in the DOM. instagram.php therefore keeps that markup in an inert
	 * <template> and shows a "Show latest posts" button instead. On click we
	 * clone the template into .ripoff-ig and let the plugin render it via its own
	 * public API (window.qliggFrontend.init), then run the a11y patch. Nothing is
	 * loaded until the visitor asks for it.
	 */
	function initInstagramLoad() {
		const container = document.querySelector('[data-ig-feed]');
		const tpl = document.querySelector('[data-ig-feed-source]');
		const loadWrap = document.querySelector('[data-ig-load]');
		const btn = document.querySelector('[data-ig-load-btn]');
		if (!container || !tpl || !btn) return;

		btn.addEventListener('click', function () {
			// Move the feed markup into the live DOM.
			container.appendChild(tpl.content.cloneNode(true));
			if (loadWrap) loadWrap.hidden = true;

			// Ask the plugin to render the feed we just inserted. If the plugin
			// JS hasn't defined its API yet, poll briefly for it.
			const render = () => {
				if (window.qliggFrontend && typeof window.qliggFrontend.init === 'function') {
					window.qliggFrontend.init(container);
					initInstagramA11y();
					return true;
				}
				return false;
			};
			if (!render()) {
				let tries = 0;
				const iv = window.setInterval(() => {
					if (render() || ++tries > 40) window.clearInterval(iv);
				}, 100);
			}
		}, { once: true });
	}

	/**
	 * A11y patch for the Instagram feed. The QuadLayers plugin renders its tile
	 * links (.instagram-gallery-item__icon) client-side with no text or label, so
	 * they fail "link-name". We give them a generic accessible name after the feed
	 * populates. Scoped to our .ripoff-ig wrapper; we don't touch the plugin.
	 */
	function initInstagramA11y() {
		const wrap = document.querySelector('.ripoff-ig');
		if (!wrap) return;

		const label = () => {
			wrap.querySelectorAll('a.instagram-gallery-item__icon:not([aria-label])').forEach((a) => {
				a.setAttribute('aria-label', 'RIP OFF på Instagram');
			});
		};

		label(); // in case it's already rendered
		// The feed builds asynchronously; watch for the tiles to appear.
		const mo = new MutationObserver(() => label());
		mo.observe(wrap, { childList: true, subtree: true });
		// Stop observing after a short window (feed is done by then).
		window.setTimeout(() => mo.disconnect(), 8000);
	}

	// Mark that JS is active BEFORE first paint of below-the-fold content, so the
	// reveal start-state (hidden) only ever applies when we can animate it.
	document.documentElement.classList.add('js-reveal');

	document.addEventListener('DOMContentLoaded', function () {
		initMobileNav();
		initScrollHeader();
		initAudioPlayers();
		initHeroSlideshow();
		initVideoCarousel();
		initScrollReveal();
		initInstagramLoad();
		initEmbedLoad();
		initSwishCopy();
	});
})();

//# sourceMappingURL=main.js.map
